import { watch, onMounted, onUnmounted, getCurrentInstance, nextTick, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { LocationQueryValue } from 'vue-router'
import type { useTriStateFilter } from './useTriStateFilter'

type TriStateFilterReturn = ReturnType<typeof useTriStateFilter>

export interface UseUrlFiltersOptions {
  /**
   * Simple filters to sync with URL.
   * Map of URL param name to ref holding the value.
   * - string values: synced as-is
   * - string[] values: synced as comma-separated
   */
  filters?: Record<string, Ref<string | string[]>>

  /**
   * Tri-state filters to sync with URL.
   * Map of base param name to tri-state filter instance.
   * Synced as: {name}_in=a,b and {name}_ex=c,d
   */
  triStateFilters?: Record<string, TriStateFilterReturn>

  /** Debounce delay in ms (default 300) */
  debounce?: number

  /** Callback when any filter changes */
  onFilterChange?: () => void

  /** Skip auto-initialization from URL (for testing) */
  skipAutoInit?: boolean
}

/**
 * Composable to sync filter state with URL query params.
 *
 * Features:
 * - Initialize filters from URL on mount
 * - Update URL when filters change (debounced)
 * - Clear params from URL when filter is empty
 * - Preserve hash (used for slideovers)
 * - Support for simple values, arrays, and tri-state filters
 */
export function useUrlFilters(options: UseUrlFiltersOptions) {
  const {
    filters = {},
    triStateFilters = {},
    debounce = 300,
    onFilterChange,
    skipAutoInit = false,
  } = options

  const route = useRoute()
  const router = useRouter()

  // Track if we're currently syncing from URL to avoid feedback loops
  let isSyncingFromUrl = false
  let debounceTimeout: ReturnType<typeof setTimeout> | null = null

  // Max items to prevent client-side DoS from malicious URLs
  const MAX_ARRAY_ITEMS = 50

  /**
   * Parse a comma-separated string into array, or return empty array if empty/null.
   * Handles both string and string[] (multi-value query params like ?a=1&a=2).
   */
  function parseArray(value: LocationQueryValue | LocationQueryValue[]): string[] {
    if (!value) return []
    if (Array.isArray(value)) {
      return value.flatMap((v) => (v ? v.split(',').filter(Boolean) : [])).slice(0, MAX_ARRAY_ITEMS)
    }
    return value.split(',').filter(Boolean).slice(0, MAX_ARRAY_ITEMS)
  }

  /**
   * Initialize filters from URL query params
   */
  function initFromUrl() {
    isSyncingFromUrl = true

    // Simple filters
    for (const [key, filterRef] of Object.entries(filters)) {
      const urlValue = route.query[key]
      if (Array.isArray(filterRef.value)) {
        filterRef.value = parseArray(urlValue)
      } else {
        filterRef.value = typeof urlValue === 'string' ? urlValue : ''
      }
    }

    // Tri-state filters
    for (const [key, triState] of Object.entries(triStateFilters)) {
      const includedKey = `${key}_in`
      const excludedKey = `${key}_ex`
      triState.included.value = parseArray(route.query[includedKey])
      triState.excluded.value = parseArray(route.query[excludedKey])
    }

    // Defer flag reset until watchers have flushed to prevent double callbacks
    void nextTick(() => {
      isSyncingFromUrl = false
    })
  }

  /**
   * Update URL with current filter state
   */
  function updateUrl() {
    const query: Record<string, string | string[] | undefined> = {}

    // Copy existing non-filter params (preserve unrelated params including multi-value)
    const filterKeys = new Set([
      ...Object.keys(filters),
      ...Object.keys(triStateFilters).flatMap((k) => [`${k}_in`, `${k}_ex`]),
    ])
    for (const [key, value] of Object.entries(route.query)) {
      if (!filterKeys.has(key)) {
        if (typeof value === 'string') {
          query[key] = value
        } else if (Array.isArray(value)) {
          query[key] = value.filter((v): v is string => typeof v === 'string')
        }
      }
    }

    // Simple filters
    for (const [key, filterRef] of Object.entries(filters)) {
      const value = filterRef.value
      if (Array.isArray(value)) {
        if (value.length > 0) {
          query[key] = value.join(',')
        }
      } else if (value) {
        query[key] = value
      }
    }

    // Tri-state filters
    for (const [key, triState] of Object.entries(triStateFilters)) {
      if (triState.included.value.length > 0) {
        query[`${key}_in`] = triState.included.value.join(',')
      }
      if (triState.excluded.value.length > 0) {
        query[`${key}_ex`] = triState.excluded.value.join(',')
      }
    }

    router.replace({ query })
  }

  /**
   * Debounced URL update
   */
  function scheduleUrlUpdate() {
    if (isSyncingFromUrl) return

    if (debounceTimeout) {
      clearTimeout(debounceTimeout)
    }

    debounceTimeout = setTimeout(() => {
      updateUrl()
      onFilterChange?.()
    }, debounce)
  }

  /**
   * Clear all filters
   */
  function clearAll() {
    isSyncingFromUrl = true

    for (const filterRef of Object.values(filters)) {
      if (Array.isArray(filterRef.value)) {
        filterRef.value = []
      } else {
        filterRef.value = ''
      }
    }

    for (const triState of Object.values(triStateFilters)) {
      triState.clear()
    }

    updateUrl()
    onFilterChange?.()

    // Defer flag reset until watchers have flushed to prevent double callbacks
    void nextTick(() => {
      isSyncingFromUrl = false
    })
  }

  /**
   * Check if any filters are active
   */
  function hasActiveFilters(): boolean {
    for (const filterRef of Object.values(filters)) {
      if (Array.isArray(filterRef.value)) {
        if (filterRef.value.length > 0) return true
      } else if (filterRef.value) {
        return true
      }
    }

    for (const triState of Object.values(triStateFilters)) {
      if (triState.hasFilters.value) return true
    }

    return false
  }

  // Watch all filter refs for changes
  for (const filterRef of Object.values(filters)) {
    watch(filterRef, scheduleUrlUpdate, { deep: true })
  }

  // Watch tri-state filters
  for (const triState of Object.values(triStateFilters)) {
    watch([triState.included, triState.excluded], scheduleUrlUpdate, { deep: true })
  }

  // Initialize from URL on mount (if in component context and not skipped)
  // Also clean up debounce timer on unmount to prevent post-teardown side effects
  if (!skipAutoInit && getCurrentInstance()) {
    onMounted(() => {
      initFromUrl()
    })

    onUnmounted(() => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout)
        debounceTimeout = null
      }
    })
  }

  return {
    /** Clear all filters and update URL */
    clearAll,
    /** Check if any filters are active */
    hasActiveFilters,
    /** Force immediate URL update (bypasses debounce) */
    updateUrlNow: updateUrl,
    /** Re-initialize filters from current URL */
    initFromUrl,
  }
}
